import { type FormatMessageLike, formatMessage } from "./i18n-airange-admin-runtime";

export type IdentityStatusCanonicalValue = "active" | "inactive";
export type IdentityStatusTone = "success" | "default" | "warning";
export type IdentityStatusValueEnumStatus = "Success" | "Default" | "Warning";
export type IdentityStatusDisplay = {
  canonicalValue: IdentityStatusCanonicalValue | undefined;
  label: string;
  tone: IdentityStatusTone;
  valueEnumStatus: IdentityStatusValueEnumStatus;
  raw: string;
  isDefined: boolean;
};

const IDENTITY_STATUS_LABEL_DESCRIPTORS = {
  active: { id: "platform.admin.identity.values.active" },
  inactive: { id: "platform.admin.identity.values.inactive" },
} as const;
const IDENTITY_STATUS_TONE_MAP: Record<IdentityStatusCanonicalValue, IdentityStatusTone> = {
  active: "success",
  inactive: "default",
};
const IDENTITY_STATUS_VALUE_ENUM_STATUS_MAP: Record<
  IdentityStatusCanonicalValue,
  IdentityStatusValueEnumStatus
> = {
  active: "Success",
  inactive: "Default",
};
const UNDEFINED_STATUS_DESCRIPTOR = {
  id: "platform.admin.identity.values.undefinedStatus",
} as const;

function createIdentityStatusDisplay(
  value: string | number | null | undefined,
  resolveLabel: FormatMessageLike,
): IdentityStatusDisplay {
  const raw = String(value ?? "").trim();
  if (raw === "active" || raw === "inactive") {
    const canonicalValue = raw as IdentityStatusCanonicalValue;
    return {
      canonicalValue,
      label: resolveLabel(IDENTITY_STATUS_LABEL_DESCRIPTORS[canonicalValue]),
      tone: IDENTITY_STATUS_TONE_MAP[canonicalValue],
      valueEnumStatus: IDENTITY_STATUS_VALUE_ENUM_STATUS_MAP[canonicalValue],
      raw,
      isDefined: true,
    };
  }
  const undefinedLabel = resolveLabel(UNDEFINED_STATUS_DESCRIPTOR);
  return {
    canonicalValue: undefined,
    label: raw ? `${undefinedLabel} (${raw})` : undefinedLabel,
    tone: "warning",
    valueEnumStatus: "Warning",
    raw,
    isDefined: false,
  };
}

export function getIdentityStatusDisplay(
  value: string | number | null | undefined,
  resolveLabel: FormatMessageLike = formatMessage,
) {
  return createIdentityStatusDisplay(value, resolveLabel);
}

export function getIdentityStatusLabel(
  value: string | number | null | undefined,
  resolveLabel?: FormatMessageLike,
) {
  return getIdentityStatusDisplay(value, resolveLabel).label;
}

export function getIdentityStatusTone(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).tone;
}

export function getIdentityStatusValueEnumStatus(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).valueEnumStatus;
}

export function normalizeIdentityStatusValue(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).canonicalValue;
}

export function buildIdentityStatusValues() {
  return ["active", "inactive"] as const;
}
